import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType } from '@azure/msal-browser';
import { of, Subject } from 'rxjs';
import { catchError, filter, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { BreadcrumbService } from './breadcrums/services/breadcrumb.service';
import { errorMessageConstants } from './core/_constants/messages.constants';
import { LoaderService } from './core/_services/loader-service/loader.service';
import { UserDataService } from './core/_services/user-data/user-data.service';
import { CommonService } from './shell/services/common/common.service';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { ConfirmDialogService } from './core/_services/confirm-dialog/confirm-dialog.service';
import { CookieService } from 'ngx-cookie-service';
import { PreventDevToolsService } from './shell/services/common/prevent-dev-tools.service';

@Component({
  selector: 'dtrax-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
  loading$ = this._loader.loading$;
  showLoading = false;
  messageConstants: any = errorMessageConstants;
  idleTime = 1800;
  timeoutSetting = 60;
  accessLevel: string | null = '';
  dashboardAccessLevel: string | null = '';
  menuItems: any = [];

  private readonly _destroying$ = new Subject<void>();

  constructor(
    private msalService: MsalService,
    public _loader: LoaderService,
    private _userDataService: UserDataService,
    private authService: MsalService,
    private broadcastService: MsalBroadcastService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private breadcrumbService: BreadcrumbService,
    private commonService: CommonService,
    private idle: Idle,
    private keepalive: Keepalive,
    private confirmDialog: ConfirmDialogService,
    private cookieService: CookieService,
    private preventDevTools: PreventDevToolsService,
    private cdr: ChangeDetectorRef,
  ) {
    // Post-logout clean reload: wipe all stale MSAL state before Angular routing
    // or MsalGuard fires. The second load (no flag) hits MsalGuard normally → B2C.
    if (sessionStorage.getItem('msal.logout.redirect') === 'true') {
      sessionStorage.clear();
      window.location.replace('/');
      return;
    }

    // MSAL initialization
    this.initMsal();
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {

        const activatedRouteSnapshot: any = this.activatedRoute.snapshot;

        // Access the URL of the activated route snapshot
        const currentRoutePath = activatedRouteSnapshot['_routerState'].url;
        if (currentRoutePath.startsWith('/')) {
          let modifiedPath = currentRoutePath.slice(1);

          const questionMarkIndex = modifiedPath.indexOf('?');
          // If a '?' is found, remove everything after it
          if (questionMarkIndex !== -1) {
            modifiedPath = modifiedPath.slice(0, questionMarkIndex);
          }
          this.breadcrumbService.setRoutePath(modifiedPath);

          this.getAccessLevelValue();
          this.getDashboardAccess();
          this.setCurrenctMenu();

          localStorage.setItem('sidebarCollapsed', JSON.stringify(false));
        }
      });
    this.router.events.subscribe((event) => {
      switch (true) {
        case event instanceof NavigationStart: {
          this.showLoading = true;
          this.cdr.markForCheck();
          break;
        }
        case event instanceof NavigationEnd:
        case event instanceof NavigationCancel:
        case event instanceof NavigationError: {
          this.showLoading = false;
          this.cdr.markForCheck();
          setTimeout(function () {
            window.scroll({
              top: 0,
              left: 0,
              behavior: 'smooth',
            });
          }, 1);
          break;
        }
        default: {
          this.showLoading = false;
          break;
        }
      }
    });
  } // CONSTRUCTOR

  private initMsal(): void {
    this.msalService.initialize().subscribe({
      next: () => {
        this.setupSessionTimeout();
        this.LoginLogout();

        if (localStorage.getItem('pendingEntraIdLogin') === 'true') {
          localStorage.removeItem('pendingEntraIdLogin');
          Object.keys(sessionStorage)
            .filter(key => key.includes('interaction.status'))
            .forEach(key => sessionStorage.removeItem(key));
          this.msalService.loginRedirect({ scopes: ['openid', 'profile', 'user.read'] });
        }
      },
      error: (error) => {
        this.commonService.warningalert('Your Session got Expired .Please login again', 'warning');
        console.error('MSAL initialization error:', error);
      },
    });
  }

  getSystemParametersValue() {
    this.commonService.getSystemParameter().subscribe(
      (systemParameter: any) => {
        this.timeoutSetting = parseInt(
          systemParameter?.find((value: any) => value.key == 'Timeout').value
        );
        this.idleTime = parseInt(
          systemParameter?.find((value: any) => value.key == 'Idle').value
        );
      },
      (error) => { }
    );
  }

  setupSessionTimeout() {
    let accessToken = this._userDataService.getAccessToken();
    if (accessToken) {
      this.idle.setIdle(1800);
      this.idle.setTimeout(60);
      this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

      this.idle.onTimeout
        .pipe(takeUntil(this._destroying$),
          catchError((error) => {
            this.commonService.warningalert('Your session has expired. Please log in again.', 'warning');
            return of(null);
          })
        )
        .subscribe(async () => {
          if (this.authService.instance.getActiveAccount() != null) {

            // Check if user is logged in
            const confirmation = this.confirmDialog.confirmDialog({
              message:
                'Your session will expire in 60 seconds. Do you want to stay logged in?',
              confirmCaption: 'Stay Logged In',
              cancelCaption: 'Logout',
              type: 'warning',
            });

            let countdown = 60;
            let timeout: any;

            timeout = setInterval(async () => {
              countdown--;
              if (countdown <= 0) {
                clearInterval(timeout);
                this.commonService.logOut(localStorage.getItem('email')).subscribe({
                  next: () => this.performLogout(),
                  error: () => this.performLogout(),
                });
              }
            }, 1000);

            confirmation
              .pipe(takeUntil(this._destroying$))
              .subscribe(async (confirmed) => {
                clearInterval(timeout);
                if (confirmed) {
                  this.idle.watch();
                } else {
                  this.commonService.logOut(localStorage.getItem('email')).subscribe({
                    next: () => this.performLogout(),
                    error: () => this.performLogout(),
                  });
                }
              });
          } else {
            // User is not logged in, no need to show logout popup
            this.commonService.logOut(localStorage.getItem('email')).subscribe({
              next: () => this.performLogout(),
              error: () => this.performLogout(),
            });
          }
        });

      this.idle.watch();
      this.keepalive.interval(15);
      this.keepalive.onPing.subscribe(() => { });
    }
  }

  async ngOnInit(): Promise<any> {
    this.getAccessLevelValue();
    if (environment?.production) this.preventDevTools.disable();
    this.getDashboardAccess();
    this.getSystemParametersValue();
    this.captureReturnUrlBeforeLogin();
    this.clearSessionValues(['companyCategories'])
  }

  clearSessionValues(values: string[]) {
    values?.forEach((item: string) => {
      sessionStorage?.removeItem(item)
    })
  }

  private captureReturnUrlBeforeLogin(): void {
    // Get the current URL or specific part of the URL
    const currentUrl = window.location.pathname + window.location.search;

    if (currentUrl.includes('/reporting/reporting-request-rsvp')) {
      // Store the return URL in a cookie
      this.cookieService.set('returnUrl', currentUrl, { expires: 1, path: '/' });
    }
  }


  async LoginLogout() {
    try {
      this.broadcastService.msalSubject$
        .pipe(
          filter(
            (msg: EventMessage) =>
              msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE
          ),
          takeUntil(this._destroying$)
        )
        .subscribe(async (result: EventMessage) => {
          this.commonService.logOut(localStorage.getItem('email')).subscribe({
            next: () => this.performLogout(),
            error: () => this.performLogout(),
          });
        });

      // TO HANDLE CANCEL CLICK IN AUTH PAGES
      this.broadcastService.msalSubject$
        .pipe(
          filter(
            (msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE
          ),
          takeUntil(this._destroying$)
        )
        .subscribe(async (result: any) => {
          if (result != null) {
            if (result.error.toString().indexOf('AADB2C90118') > -1) {
              const url = `${environment.changePasswordAuthority}/oauth2/authorize?client_id=${environment.clientID}
            &redirect_uri=${environment.redirectURI}
            &response_type=id_token&scope=openid`;
              window.location.href = url;
            } else {
              this.authService.loginRedirect();
            }
          }
        });
      // TO GET BEARER TOKEN FOR HTTP INTERCEPTOR
      let users = this.authService.instance.getAllAccounts();

      if (users.length > 0) {
        this.authService.instance.setActiveAccount(users[0]);

        this.authService
          .acquireTokenSilent({ scopes: environment.b2cScopes })
          .subscribe((res) => { });
      } // IF

      // TO STORE BEARER TOKEN FOR HTTP INTERCEPTOR
      this.broadcastService.msalSubject$
        .pipe(
          filter(
            (msg: EventMessage) =>
              msg.eventType === EventType.LOGIN_SUCCESS ||
              msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
          ),
          takeUntil(this._destroying$)
        )
        .subscribe(async (result: any) => {
          const accessToken = result.payload?.accessToken;
          this._userDataService.tokenData = result.payload;
          this._userDataService.setAccessToken(accessToken);
          this.commonService.getUserDetails();
        });

    } catch (error) { }
  }

  private performLogout(): void {
    const account = this.msalService.instance.getActiveAccount()
      ?? this.msalService.instance.getAllAccounts()[0];
    this._userDataService.clearAccessToken();
    sessionStorage.clear();
    localStorage.clear();
    sessionStorage.setItem('msal.logout.redirect', 'true');
    this.msalService.logoutRedirect({
      account,
      postLogoutRedirectUri: environment.logoutURI,
    });
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  //To get AccessLevel value
  getAccessLevelValue() {
    this.commonService.getAccessLevel().subscribe(
      (accessLevel: string | null) => {
        if (accessLevel) {
          this.accessLevel = accessLevel;
        }
      },
      (error) => { }
    );
  }

  // get Dashboard access level for corporate CT1 if dashboard submenu is comming
  getDashboardAccess() {
    this.commonService.getDashboardAccess().subscribe(
      (dashboardAccessLevel: string | null) => {
        if (dashboardAccessLevel) {
          this.dashboardAccessLevel = dashboardAccessLevel;
        }
      },
      (error) => { }
    );
  }

  setCurrenctMenu = () => {
    try {
      const getMenus: any = localStorage.getItem('menuItems');
      this.menuItems = JSON.parse(getMenus);

      let t1Dashboard = this.menuItems?.find(
        (menuItems: any) => menuItems?.menU_NAME == 'T1 Dashboard'
      );
      let corporateDashboard = this.menuItems?.find(
        (menuItems: any) => menuItems?.menU_NAME == 'Corporate Dashboard'
      );
      if (this.breadcrumbService.getRoutePath() === 'dashboard') {
        if (
          this.accessLevel == 'CT1' &&
          this.dashboardAccessLevel == 'corporate-dashboard'
        ) {
          this.breadcrumbService.setRoutePath(corporateDashboard?.actioN_PATH);
        } else if (
          this.accessLevel == 'CT1' &&
          this.dashboardAccessLevel == 't1-dashboard'
        ) {
          this.breadcrumbService.setRoutePath(t1Dashboard?.actioN_PATH);
        }
      }

      let currentMenu = this.menuItems?.find(
        (menu: any) => menu?.actioN_PATH == this.breadcrumbService.getRoutePath()
      );

      if (
        this.accessLevel == 'CT1' &&
        this.dashboardAccessLevel == 'corporate-dashboard'
      ) {
        let activeMenuStringfy = localStorage.getItem('activeMenu');
        let activeMenu = activeMenuStringfy
          ? JSON.parse(activeMenuStringfy)
          : null;

        if (
          activeMenu?.actioN_PATH == 'dashboard' &&
          activeMenu?.menU_TYPE == 'T'
        ) {
          currentMenu = corporateDashboard;
        }
      }
      if (
        this.accessLevel == 'CT1' &&
        this.dashboardAccessLevel == 't1-dashboard'
      ) {
        let activeMenuStringfy = localStorage.getItem('activeMenu');
        let activeMenu = activeMenuStringfy
          ? JSON.parse(activeMenuStringfy)
          : null;
        if (
          activeMenu?.actioN_PATH == 'dashboard' &&
          activeMenu?.menU_TYPE == 'T'
        ) {
          currentMenu = t1Dashboard;
        }
      }
      if (currentMenu != undefined || currentMenu != null) {
        localStorage.setItem('activeMenu', JSON.stringify(currentMenu));
      }
    } catch (error) {
      console.error(error)
    }
  };
} // EXPORT
